import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CONSOLE_CATALOGUE,
  CONSOLE_GROUP_META,
  CONSOLE_GROUP_ORDER,
  cardToAction,
  type ConsoleCard,
  type ConsoleGroup,
} from '@/data/consoleCatalogue';
import {
  openControlPort,
  makeCommand,
  makeHello,
  EMPTY_ROSTER,
  type ControlAck,
  type ControlStatus,
  type RosterEntry,
} from '@/events/controlChannel';
import { SCRIPTED_EVENTS } from '@/data/scriptedEvents';
import { SIM_START_ISO } from '@/state/simulationStore';
import SearchSelect from '@/components/SearchSelect';
import { RotateCw, Play, Radio, Clock, Crosshair } from 'lucide-react';
import clsx from 'clsx';

/** The demo day runs 4 May 2026; the seed clock starts at 1pm. Presets walk the
 *  day so the facilitator can jump straight to the state a screen reads best in,
 *  and the last one rolls over into the next morning. */
const DAY = '2026-05-04';
const NEXT_DAY = '2026-05-05';
/** Where the seed opens — taken from the store itself, not re-typed, so
 *  "Restore the demo day" can never drift from the clock the day is authored
 *  around (and honours VITE_SIM_START_ISO like everything else). */
const SEED_START_ISO = SIM_START_ISO;
const CLOCK_PRESETS = [
  { label: '09:00', iso: `${DAY}T09:00:00+10:00` },
  { label: '11:00', iso: `${DAY}T11:00:00+10:00` },
  { label: '13:00', iso: `${DAY}T13:00:00+10:00` },
  { label: '15:30', iso: `${DAY}T15:30:00+10:00` },
  { label: '17:00', iso: `${DAY}T17:00:00+10:00` },
  { label: 'Tomorrow 09:00', iso: `${NEXT_DAY}T09:00:00+10:00` },
];
const ROLLOVER_ISO = `${NEXT_DAY}T09:00:00+10:00`;

/** The scripted beats as pickable options, time-prefixed so the list reads in
 *  running order and a search for "16:00" or "voicemail" both land. */
const BEAT_OPTIONS = SCRIPTED_EVENTS.map((beat) => ({
  id: beat.id,
  label: `${beat.triggerIso.slice(11, 16)} · ${beat.description}`,
}));

/** Which specific records commands should act on. Every field optional: an empty
 *  aim means each handler falls back to the first record that fits. */
interface Aim {
  studioId?: string;
  artistId?: string;
  customerId?: string;
  bookingId?: string;
  depositId?: string;
}

/**
 * Second-screen control console. A PURE remote: it posts commands over the
 * control channel; the app tab (useControlBridge in AppShell) executes them.
 * Open this at /console in its own window beside the running app.
 */
export default function ControlConsole() {
  const portRef = useRef<ReturnType<typeof openControlPort> | null>(null);
  const [status, setStatus] = useState<ControlStatus | null>(null);
  const [activeGroup, setActiveGroup] = useState<ConsoleGroup | 'all'>('all');
  const [beatId, setBeatId] = useState<string>(SCRIPTED_EVENTS[0]?.id ?? '');
  const [aim, setAim] = useState<Aim>({});
  // Which card each in-flight command came from, and the last answer per card.
  const [outcome, setOutcome] = useState<{
    pending: Record<string, string>;
    byCard: Record<string, ControlAck>;
  }>({ pending: {}, byCard: {} });

  useEffect(() => {
    const port = openControlPort();
    portRef.current = port;
    const unsub = port.subscribe((msg) => {
      if (msg.kind === 'status') setStatus(msg);
      if (msg.kind === 'ack') {
        setOutcome((sent) => {
          const cardId = sent.pending[msg.commandId];
          if (!cardId) return sent;
          const { [msg.commandId]: _done, ...rest } = sent.pending;
          return { pending: rest, byCard: { ...sent.byCard, [cardId]: msg } };
        });
      }
    });
    port.post(makeHello(new Date().toISOString())); // hello ts is display-only, not domain time
    return () => {
      unsub();
      port.close();
      portRef.current = null;
    };
  }, []);

  const connected = status !== null;
  const firedIds = useMemo(() => new Set(status?.firedEventIds ?? []), [status]);
  const roster = status?.roster ?? EMPTY_ROSTER;

  /**
   * What each command is aimed at. Empty means "whatever fits" — the handler
   * picks the first suitable record, which is what you want when demoing the
   * shape of a flow. Set one and every command carries it, which is what you
   * want when the client asks "do that for MY booking".
   */
  const aimPayload = useMemo(() => {
    const p: Record<string, unknown> = {};
    if (aim.studioId) p.studioId = aim.studioId;
    if (aim.artistId) p.artistId = aim.artistId;
    if (aim.customerId) p.customerId = aim.customerId;
    if (aim.bookingId) p.bookingId = aim.bookingId;
    if (aim.depositId) p.depositId = aim.depositId;
    return p;
  }, [aim]);

  const aimedCount = Object.keys(aimPayload).length;

  const send = (card: ConsoleCard) => {
    const { action, payload } = cardToAction(card);
    const ts = status?.clockIso ?? new Date().toISOString();
    // The card's own payload wins over the aim: a card that says $200 means $200.
    const command = makeCommand({
      action,
      payload: { ...aimPayload, ...payload },
      navigateTo: card.navigateTo,
      ts,
    });
    setOutcome((sent) => ({ ...sent, pending: { ...sent.pending, [command.id]: card.id } }));
    portRef.current?.post(command);
  };

  const sendGlobal = (action: string, payload?: Record<string, unknown>) => {
    const ts = status?.clockIso ?? new Date().toISOString();
    portRef.current?.post(makeCommand({ action, payload, ts }));
  };

  const totalFired = (status?.counts.fired ?? 0) + (status?.counts.remaining ?? 0);

  return (
    <div className="min-h-screen bg-ink-black text-cream" data-testid="control-console">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="mb-6 flex items-start justify-between gap-4">
          <div>
            <p className="label-small-caps text-gold-bright">Control console</p>
            <h1 className="heading-display text-3xl text-cream mt-2">Second-screen console</h1>
            <p className="font-serif text-cream-soft mt-2 max-w-2xl">
              Open the app in another window. Every button here fires there.
            </p>
          </div>
          <div
            className={clsx(
              'flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs',
              connected ? 'border-mint/50 text-mint' : 'border-blood/50 text-blood',
            )}
            data-testid="console-connection"
          >
            <Radio size={13} />
            {connected ? (
              <span>Connected · {status?.clockIso.slice(11, 16)} · {status?.counts.fired}/{totalFired} fired</span>
            ) : (
              <span>No app connected — open the app in another window</span>
            )}
          </div>
        </header>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="console-globals">
          {/* Named for what it actually does. `simulationApi.reset()` reloads the
              fixtures AND puts the clock back to the seed's opening time, so this
              is the one way out of a session where the clock has wandered and the
              floor reads "everyone is late". */}
          <button
            type="button"
            className="btn-quiet text-xs"
            data-testid="console-restore"
            onClick={() => sendGlobal('global:reset')}
          >
            <RotateCw size={12} /> Restore the demo day ({SEED_START_ISO.slice(11, 16)})
          </button>
          <button type="button" className="btn-quiet text-xs" onClick={() => sendGlobal('global:play')}>
            <Play size={12} /> Next beat
          </button>
          <button type="button" className="btn-quiet text-xs" onClick={() => sendGlobal('global:rollover', { iso: ROLLOVER_ISO })}>
            Day rollover
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="console-clock-presets">
          <span className="label-small-caps text-cream-faint">Clock</span>
          {CLOCK_PRESETS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="btn-quiet text-xs"
              data-testid={`console-clock-${preset.label}`}
              onClick={() => sendGlobal('global:set-clock', { iso: preset.iso })}
            >
              <Clock size={12} /> {preset.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="console-beat-picker">
          <span className="label-small-caps text-cream-faint">Beat</span>
          <div className="w-full max-w-md">
            <SearchSelect
              options={BEAT_OPTIONS}
              value={beatId}
              onSelect={(id) => setBeatId(id ?? BEAT_OPTIONS[0]?.id ?? '')}
              noun="beat"
              placeholder="Pick a scripted beat"
              testid="console-beat-select"
            />
          </div>
          <button
            type="button"
            className="btn-quiet text-xs"
            disabled={!connected}
            onClick={() => sendGlobal('global:beat', { eventId: beatId })}
          >
            Fire beat
          </button>
        </div>

        <section className="panel p-4 mb-6" data-testid="console-aim">
          <div className="flex items-baseline justify-between gap-3 mb-3">
            <div>
              <p className="label-small-caps text-gold-bright flex items-center gap-2">
                <Crosshair size={12} /> Aim
              </p>
              <p className="font-serif text-sm text-cream-soft mt-1">
                {aimedCount === 0
                  ? 'Nothing aimed — each trigger acts on the first record that fits.'
                  : `Every trigger below is aimed at ${aimedCount} chosen ${aimedCount === 1 ? 'record' : 'records'}.`}
              </p>
            </div>
            {aimedCount > 0 ? (
              <button type="button" className="btn-quiet text-xs" onClick={() => setAim({})}>
                Clear aim
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <AimSelect label="Studio" options={roster.studios} value={aim.studioId}
              onChange={(v) => setAim((a) => ({ ...a, studioId: v }))} />
            <AimSelect label="Artist" options={roster.artists} value={aim.artistId}
              onChange={(v) => setAim((a) => ({ ...a, artistId: v }))} />
            <AimSelect label="Client" options={roster.customers} value={aim.customerId}
              onChange={(v) => setAim((a) => ({ ...a, customerId: v }))} />
            <AimSelect label="Booking" options={roster.bookings} value={aim.bookingId}
              onChange={(v) => setAim((a) => ({ ...a, bookingId: v }))} />
            <AimSelect label="Deposit" options={roster.deposits} value={aim.depositId}
              onChange={(v) => setAim((a) => ({ ...a, depositId: v }))} />
          </div>
        </section>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="console-group-filter">
          <GroupChip active={activeGroup === 'all'} label="All" onClick={() => setActiveGroup('all')} />
          {CONSOLE_GROUP_ORDER.map((g) => (
            <GroupChip key={g} active={activeGroup === g} label={CONSOLE_GROUP_META[g].label} onClick={() => setActiveGroup(g)} />
          ))}
        </div>

        {CONSOLE_GROUP_ORDER.filter((g) => activeGroup === 'all' || activeGroup === g).map((g) => {
          const cards = CONSOLE_CATALOGUE.filter((c) => c.group === g);
          if (cards.length === 0) return null;
          return (
            <section key={g} className="mb-8" data-testid={`console-section-${g}`}>
              <header className="mb-3">
                <h2 className="font-cinzel text-lg text-cream">{CONSOLE_GROUP_META[g].label}</h2>
                <p className="font-serif text-cream-soft text-sm mt-0.5">{CONSOLE_GROUP_META[g].story}</p>
              </header>
              <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {cards.map((card) => {
                  const fired = card.oneShotEventId ? firedIds.has(card.oneShotEventId) : false;
                  const ack = outcome.byCard[card.id];
                  return (
                    <li key={card.id}>
                      <button
                        type="button"
                        disabled={!connected || fired}
                        onClick={() => send(card)}
                        data-testid={`console-card-${card.id}`}
                        className={clsx(
                          'w-full text-left panel transition p-4 h-full flex flex-col gap-2 disabled:opacity-40 disabled:cursor-not-allowed',
                          ack && !ack.ok
                            ? 'border-blood/60 hover:border-blood'
                            : 'hover:border-gold-bright/60',
                        )}
                      >
                        <span className="flex items-start gap-3">
                          <span className="text-2xl shrink-0" aria-hidden="true">{card.emoji}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-inter text-base text-cream leading-snug">{card.title}</span>
                            <span className="block font-serif text-sm text-cream-soft mt-1 leading-snug">{card.subtitle}</span>
                          </span>
                        </span>
                        {card.movesClock ? (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-orange">
                            <Clock size={10} /> Moves the clock
                          </span>
                        ) : null}
                        {fired ? <span className="text-[10px] uppercase tracking-widest text-cream-faint">Fired</span> : null}
                        {ack ? (
                          <span
                            className={clsx(
                              'block font-inter text-xs leading-snug',
                              ack.ok ? 'text-mint' : 'text-blood',
                            )}
                            data-testid={`console-ack-${card.id}`}
                          >
                            {ack.ok ? '✓ Done' : `✗ ${ack.note ?? 'Did not run'}`}
                          </span>
                        ) : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function AimSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: RosterEntry[];
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const noun = label.toLowerCase();
  return (
    <div className="flex items-center gap-2 text-xs min-w-0">
      <span className="label-small-caps text-cream-faint w-16 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">
        <SearchSelect
          options={options}
          value={value}
          onSelect={onChange}
          disabled={options.length === 0}
          noun={noun}
          clearLabel={`Any ${noun}`}
          placeholder={options.length === 0 ? 'none available' : `Any ${noun}`}
          testid={`console-aim-${noun}`}
        />
      </div>
    </div>
  );
}

function GroupChip({ active, label, onClick }: { active: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'rounded-md border px-3 py-1.5 font-inter text-[11px] uppercase tracking-widest transition',
        active ? 'border-gold-bright bg-gold/10 text-gold-bright' : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/50',
      )}
    >
      {label}
    </button>
  );
}
